import styled from '@emotion/styled';
import {
  Loader,
  Pagination,
  TextInput,
  Link,
  Stack,
  Switch,
  Tooltip,
  Table,
  tokens,
} from '@hotjar/hotsauce';
import { isOffsite } from '@hotjar/feedback/domain/Surveys/Survey/SurveyLocations';
import { getSurveyExternalUrl } from '@hotjar/common';
import { epochDateTime, timeAgo } from '@hotjar/webapp/lib/time';
import { localeNumber } from '@hotjar/insights-common/lib/number';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { TrackingStateIcon } from '@hotjar/webapp/components/TrackingStateIcon';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import type { ReactNode } from 'react';
import { useTopNavHeight } from '@hotjar/webapp/hooks/useTopNavHeight';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { Page } from '@hotjar/webapp/components/Page';
import { useTranslation } from 'react-i18next';
import { useSurveysList } from '../hooks/useSurveysList';
import type { SortKeys } from '../hooks/useSurveysListSorting';
import { SurveyActionsDropdown } from './SurveyActionsDropdown';
import { ViewResponsesButton } from './ViewResponseButton';

const StyledLoader = styled(Loader)`
  height: 100%;
`;

const StyledPage = styled(Page)`
  width: 100%;
`;

const StyledTableCell = styled(Table.Cell)`
  vertical-align: middle;
`;

const TrackingStatus = styled.div`
  text-align: center;
`;

const FilterContainer = styled.div`
  width: 300px;
  padding-bottom: ${tokens.spacer.x3};
`;

const StatusSwitch = styled(Switch)`
  margin-bottom: 0;
`;

const PaginationContainer = styled.div`
  width: 100%;
  height: 100px;
  display: flex;
  justify-content: right;
  margin-bottom: ${tokens.spacer.x5};
`;

export function SurveysList(): JSX.Element | null {
  const {
    currentSite,
    surveys,
    sortState,
    page,
    itemsPerPage,
    totalSurveys,
    isLoading,
    setPage,
    setItemsPerPage,
    onSortChanged,
    onToggleSurveyStatus,
    filter,
    setFilter,
  } = useSurveysList();
  // FIXME: Move this component outside SurveysList.
  // eslint-disable-next-line react/no-unstable-nested-components
  const SortHeader = ({
    title,
    label,
    sortKey,
    children,
    ...headerProps
  }: {
    title: string;
    label?: string;
    sortKey: SortKeys;
    children?: ReactNode | ReactNode[];
  }) => {
    return (
      <Table.HeadCell
        {...headerProps}
        title={title}
        aria-label={label ?? title}
        isSortable
        sortLabel={`Sort ${title} column`}
        sortDirection={sortState.key === sortKey ? sortState.direction : 'none'}
        onSort={(newDirection) => onSortChanged(sortKey, newDirection)}
      >
        {children ?? title}
      </Table.HeadCell>
    );
  };

  const { t } = useTranslation('feedback');

  const canUpdateSurvey = usePermission('surveys-update');

  const trackingCode = useTrackingCode();

  const topNavHeight = useTopNavHeight();

  if (currentSite === undefined) {
    return null;
  }

  const trackingCodeState = trackingCode.getState(currentSite.lastData);

  const getSurveyRoute = (siteId: number, surveyId: number) => {
    return getRoute(SURVEYS.RESPONSES.path, { siteId, surveyId });
  };

  return (
    <StyledLoader isLoading={isLoading} size="l">
      <StyledPage>
        <FilterContainer>
          <TextInput
            value={filter}
            onChange={({ target }) => setFilter(target.value)}
            placeholder={t('survey-list.header.search-placeholder')}
            icon="search"
          />
        </FilterContainer>
        <Table caption={`${surveys.length} surveys`} tableName={t('survey-list.table.name')}>
          <Table.Head size="s">
            <Table.HeadRow stickyOffset={topNavHeight}>
              <SortHeader sortKey="enabled" title={t('survey-list.table.header.status')} />
              <SortHeader sortKey="name" title={t('survey-list.table.header.name')} />
              <SortHeader
                sortKey="response_count"
                title={t('survey-list.table.header.responses')}
              />
              <SortHeader
                sortKey="created_by_user_name"
                title={t('survey-list.table.header.created-by')}
              />
              <SortHeader sortKey="created_epoch_time" title={t('survey-list.table.header.date')} />
              <SortHeader sortKey="display_type" title={t('survey-list.table.header.location')} />
              <Table.HeadCell title={t('survey-list.table.header.actions')}>
                {t('survey-list.table.header.actions')}
              </Table.HeadCell>
            </Table.HeadRow>
          </Table.Head>
          <Table.Body>
            {surveys.map((survey) => {
              return (
                <Table.Row key={survey.id}>
                  <StyledTableCell width="1px">
                    {trackingCodeState.installed && (
                      <Tooltip
                        placement="top"
                        isDisabled={canUpdateSurvey}
                        hideOnClick={false}
                        text={t('survey-list.table.no-edit-permission')}
                      >
                        <StatusSwitch
                          isDisabled={!canUpdateSurvey}
                          isChecked={survey.enabled}
                          onChange={() => onToggleSurveyStatus(survey)}
                          size="m"
                        />
                      </Tooltip>
                    )}
                    {!trackingCodeState.installed && (
                      <TrackingStatus>
                        <TrackingStateIcon status="neverActive" extra-class="man" />
                      </TrackingStatus>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Link href={getSurveyRoute(currentSite.id, survey.id)} kind="inline">
                      {survey.name}
                    </Link>
                  </StyledTableCell>
                  <StyledTableCell width="1px">
                    {survey.response_count === 0
                      ? t('survey-list.table.cell.count-none')
                      : localeNumber(survey.response_count)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {survey.created_by_user_name !== null ? (
                      <span data-hj-suppress>{survey.created_by_user_name}</span>
                    ) : (
                      <span>{t('survey-list.table.account-deleted')}</span>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Tooltip
                      text={epochDateTime(survey.created_epoch_time)}
                      placement="top"
                      appendTo="parent"
                    >
                      <span>{timeAgo(survey.created_epoch_time)}</span>
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell>
                    {isOffsite(survey.display_type) ? (
                      <Tooltip text={getSurveyExternalUrl(survey.uuid)}>
                        <span>
                          <Link href={getSurveyExternalUrl(survey.uuid)} kind="inline" isExternal>
                            {t(`survey.display-type.${survey.display_type}`)}
                          </Link>
                        </span>
                      </Tooltip>
                    ) : (
                      t(`survey.display-type.${survey.display_type}`)
                    )}
                  </StyledTableCell>
                  <StyledTableCell width="1px">
                    <Stack spacing="x2">
                      <ViewResponsesButton href={getSurveyRoute(currentSite.id, survey.id)} />
                      <SurveyActionsDropdown survey={survey} siteId={currentSite.id} />
                    </Stack>
                  </StyledTableCell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
        <PaginationContainer>
          <Pagination
            currentPage={page}
            totalCount={totalSurveys}
            pageSize={itemsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setItemsPerPage}
          />
        </PaginationContainer>
      </StyledPage>
    </StyledLoader>
  );
}
